// The calculation's workspace: the title row (connection type, verdict and standard badges, report button, the menu
// button of the drawer), then the 3D view, compatible solutions, screw positioning and report cards, with the sticky
// results column.
import cx from "classnames";
import { memo, useEffect, useId, useRef, useState, type FC, type RefObject } from "react";
import { Accordion } from "../../../components/ds2/Accordion";
import { Badge } from "../../../components/ds2/Badge";
import { Button } from "../../../components/ds2/Button";
import { MenuIcon } from "../../../components/ds2/icons";
import { Switch } from "../../../components/ds2/Switch";
import useI18n from "../../../hooks/useTranslations";
import type { ConnectionType, SolutionRow } from "../../../http/screwConnector/formulas";
import type { LanguageCode } from "../../../types";
import { useSectionTitles, useThreeLabels } from "./labels";
import { missingFieldInfo } from "./missingFields";
import PositionCard from "./PositionCard";
import ReportCard, { type ReportCardHandle } from "./ReportCard";
import ReportLanguageMenu from "./ReportLanguageMenu";
import type { ReportVersion } from "./reportPayload";
import ResultsColumn from "./ResultsColumn";
import styles from "./ScrewConnectorResults.module.css";
import { resultsStatus } from "./sectionStatus";
import SolutionsCard, { type SolutionsView } from "./SolutionsCard";
import type { InputSectionId } from "./types";
import type { EngineView } from "./uiState";
import type { ReportSource } from "./useReportPdf";
import type { SolutionsSearch } from "./useSolutionsSearch";
import ScrewConnector3DView, { type ScrewConnector3DViewHandle } from "./ScrewConnector3DView";

const DOC_TAG = "ETA-11/0030 · EN 1995-1-1";

type ScrewConnectorResultsProps = {
  view: EngineView;
  connectionType: ConnectionType;
  // the drawer of the sidebar (under 1180px), opened from the title row
  navOpen: boolean;
  onToggleNav: () => void;
  // title button: the version selected in the report card, in the language picked from its menu
  onGenerateReport: (language: LanguageCode) => void;
  isReportPrinting: boolean;
  reportVersion: ReportVersion;
  onReportVersionChange: (version: ReportVersion) => void;
  // what the report card sends to the server besides the engine view's state
  report: Omit<ReportSource, "state" | "blockReason">;
  reportCardRef: RefObject<ReportCardHandle | null>;
  view3dRef: RefObject<ScrewConnector3DViewHandle | null>;
  // the 3D and solutions cards' collapsed state: the sidebar links and the tutorial open them too
  drawCollapsed: boolean;
  onToggleDraw: () => void;
  solutionsCollapsed: boolean;
  onToggleSolutions: () => void;
  solutions: SolutionsSearch;
  onApplySolution: (row: SolutionRow) => void;
  // "missing fields" link: opens the field's panel and focuses the field
  onFocusField: (control: string, section: InputSectionId) => void;
};

// Memoized, with stable callbacks from the page: it re-renders with the engine view (every input change), the
// solution search and the page state it shows.
const ScrewConnectorResults: FC<ScrewConnectorResultsProps> = ({
  view,
  connectionType,
  navOpen,
  onToggleNav,
  onGenerateReport,
  isReportPrinting,
  reportVersion,
  onReportVersionChange,
  report,
  reportCardRef,
  view3dRef,
  drawCollapsed,
  onToggleDraw,
  solutionsCollapsed,
  onToggleSolutions,
  solutions,
  onApplySolution,
  onFocusField,
}) => {
  const t = useI18n().translation.screwConnectorModule.calculation;
  const labels = useThreeLabels(connectionType);
  const sectionTitles = useSectionTitles(connectionType);
  const [showQuote, setShowQuote] = useState(true);
  const showDimsId = useId();
  const { state, blockReason } = view;

  // the visible height of the workspace bounds the sticky results column
  const workspaceRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const workspace = workspaceRef.current;
    if (!workspace) return;
    const update = () => workspace.style.setProperty("--sc-workspace-h", `${workspace.clientHeight}px`);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(workspace);
    return () => observer.disconnect();
  }, []);

  const verdict = resultsStatus(view.ui, blockReason);
  const badge =
    verdict === "ok"
      ? { color: "success" as const, text: t.status.verified }
      : verdict === "bad"
        ? { color: "error" as const, text: t.status.notVerified }
        : { color: "grey" as const, text: t.status.pending };

  // "Visualizzazione 3D" header: the θ branch of the drawing
  const theta = state && Number.isFinite(state.theta) ? state.theta : null;
  const branchMeta =
    theta === null ? t.view.branchPending : t.view.branchMeta(theta.toFixed(0), theta <= 90 ? "0°–90°" : "90°–180°");

  const solutionsResult = solutions.result;
  const solutionsView: SolutionsView | null = !solutionsResult
    ? null
    : solutionsResult.kind === "missing"
      ? {
          kind: "missing",
          fields: solutionsResult.missing.map((control) => {
            const { label, section } = missingFieldInfo(control, t);
            return { label, panel: sectionTitles[section], onFocus: () => onFocusField(control, section) };
          }),
        }
      : {
          kind: "done",
          candidates: solutionsResult.totalCandidates,
          found: solutionsResult.totalSolutions,
          rows: solutionsResult.top,
          selectNew: solutionsResult.selectNewSolution,
        };

  return (
    <div ref={workspaceRef} className={styles.workspace}>
      <div className={styles.content}>
        <div className={styles.pageTitle}>
          {/* shown only when the sidebar is a drawer */}
          <span className={styles.menuToggle}>
            <Button
              variant="plain"
              aria-label={navOpen ? t.shell.closeMenu : t.shell.openMenu}
              aria-expanded={navOpen}
              onClick={onToggleNav}
            >
              <MenuIcon width={22} height={22} />
            </Button>
          </span>
          <div className={styles.titleText}>
            <h1>{labels.pageTitle}</h1>
          </div>
          <div className={styles.titleActs}>
            <Badge color={badge.color} aria-live="polite">
              {badge.text}
            </Badge>
            <Badge color="info">{DOC_TAG}</Badge>
            <ReportLanguageMenu label={t.shell.generateReport} onPick={onGenerateReport}>
              {({ open, toggle }) => (
                <Button
                  variant="secondary"
                  isLoading={isReportPrinting}
                  aria-haspopup="menu"
                  aria-expanded={open}
                  onClick={toggle}
                >
                  {t.shell.generateReport}
                </Button>
              )}
            </ReportLanguageMenu>
          </div>
        </div>

        <div id="secRisultati" className={cx(styles.viewResultsRow, styles.slot)}>
          <div className={styles.viewCol}>
            <div id="drawCard" className={cx(styles.slot, styles.drawSlot)}>
              {/* the viewer stays mounted while closed */}
              <Accordion
                title={t.view.title}
                extra={
                  <div className={styles.drawExtra}>
                    {/* the switch's click stops here: in the Accordion's summary it would toggle the card */}
                    <div className={styles.drawSwitch} onClick={(e) => e.stopPropagation()}>
                      <Switch id={showDimsId} checked={showQuote} onChange={setShowQuote}>
                        {t.view.showDims}
                      </Switch>
                    </div>
                    {branchMeta}
                  </div>
                }
                open={!drawCollapsed}
                onToggle={onToggleDraw}
              >
                <ScrewConnector3DView
                  ref={view3dRef}
                  state={state}
                  connectionType={connectionType}
                  showQuote={showQuote}
                  blocked={blockReason}
                />
              </Accordion>
            </div>
            <div id="solutionsCard" className={cx(styles.slot, styles.solutionsSlot)}>
              <SolutionsCard
                predrilled={solutions.predrilled}
                washer={solutions.washer}
                onPredrilledChange={solutions.setPredrilled}
                onWasherChange={solutions.setWasher}
                running={solutions.running}
                progress={solutions.progress}
                result={solutionsView}
                onSearch={solutions.search}
                onApply={onApplySolution}
                collapsed={solutionsCollapsed}
                onToggleCollapsed={onToggleSolutions}
              />
            </div>
            <div id="posCard" className={cx(styles.slot, styles.posSlot)}>
              <PositionCard state={state} blockReason={blockReason} connectionType={connectionType} />
            </div>
            <div id="reportCard" className={cx(styles.slot, styles.reportSlot)}>
              <ReportCard
                ref={reportCardRef}
                {...report}
                state={state}
                blockReason={blockReason}
                version={reportVersion}
                onVersionChange={onReportVersionChange}
              />
            </div>
          </div>

          <div className={styles.resultsCol}>
            <ResultsColumn state={state} blockReason={blockReason} violations={view.ui.violations} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(ScrewConnectorResults);
